import { Component, OnInit, ChangeDetectorRef, EventEmitter, Output, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { ApiService } from '@/app/shared/service/api.service';
import { UsersService } from '@/app/components/services/users/users.service';

@Component({
  selector: 'app-config-registro',
  templateUrl: './config-registro.component.html',
  styleUrls: ['./config-registro.component.scss']
})
export class ConfigRegistroComponent implements OnInit {

  @Input() configId: number;
  @Output() regresar = new EventEmitter<void>();

  // Props del formulario principal
  public form: FormGroup;
  public submitted = false;
  public isLoading = false;
  public isEditMode = false;
  public configData: any = null;

  // Configuración de modales
  private modalConfig: any = {
    ariaLabelledBy: 'modal-basic',
    size: 'lg',
    centered: true
  };
  private activeModal: NgbModalRef = null;

  // Datos del usuario y configuración
  public currentUser: any;
  public userDetails: any;
  public isLoadingConfig: boolean = true;
  public isConfigLoaded: boolean = false;

  // Subs para manejo de memoria
  private subs: Array<Subscription> = [];

  constructor(
    private cdr: ChangeDetectorRef,
    private fb: FormBuilder,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private modalService: NgbModal,
    private toastr: ToastrService,
    private apiService: ApiService,
    private usersService: UsersService
  ) {
  }

  ngOnInit(): void {
    // Obtener datos del usuario actual
    this.loadCurrentUser();

    // Verificar si es modo edición o creación
    if (this.configId) {
      this.isEditMode = true;
      this.loadConfigData(this.configId);
    } else {
      this.buildForm();
    }

    // Suscripción a cambios de ruta
    this.subs.push(
      this.activatedRoute.params.subscribe(params => {
        if (params.id) {
          this.configId = parseInt(params.id);
          this.isEditMode = true;
          this.loadConfigData(this.configId);
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub: Subscription) => sub.unsubscribe());
  }

  /**
   * Regresar a la lista anterior
   */
  regresarALista(): void {
    this.regresar.emit();
    if (!this.regresar.observers.length) {
      this.router.navigate(['/settings']);
    }
  }

  /**
   * Cargar datos del usuario actual
   */
  private loadCurrentUser(): void {
    try {
      const userStr = localStorage.getItem('currentUser');
      if (userStr) {
        this.currentUser = JSON.parse(userStr);
        this.loadUserDetails(this.currentUser.id);
      }
    } catch (error) {
      console.error('Error al cargar usuario actual:', error);
    }
  }

  /**
   * Cargar detalles del usuario
   */
  private loadUserDetails(userId: number): void {
    this.apiService.getUserData(userId.toString()).subscribe(
      (user) => {
        this.userDetails = user;
        this.cdr.detectChanges();
      },
      (error) => {
        console.error('Error al cargar detalles del usuario:', error);
      }
    );
  }

  /**
   * Construir formulario principal
   */
  private buildForm(): void {
    this.form = this.fb.group({
      // Información básica
      name: ['', [Validators.required, Validators.minLength(3)]],
      code: ['', [Validators.required]],
      description: ['', [Validators.required, Validators.minLength(10)]],
      
      // Configuración
      isActive: [true],
      isVisible: [true],
      isOnline: [false],
      
      // Metadata
      type: [''],
      category: [''],
      
      // Contacto y ubicación
      contactName: [''],
      contactPhone: [''],
      contactEmail: ['', Validators.pattern('^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$')],
      address: [''],
      
      // Fechas y vigencia
      validityDays: [0],
      validityType: ['Días naturales'],
      answerTime: [0],
      answerTimeType: ['Días hábiles'],
      
      // Configuración avanzada
      office: [''],
      location: [''],
      schedule: [''],
      notes: ['']
    });

    this.isLoadingConfig = false;
    this.isConfigLoaded = true;
  }

  /**
   * Cargar datos de configuración existente
   */
  private loadConfigData(configId: number): void {
    this.isLoadingConfig = true;
    
    // Ejemplo de carga de datos - adaptar según el servicio específico
    this.apiService.getConfigById(configId).subscribe(
      (data: any) => {
        this.configData = data;
        this.setFormValues(data);
        this.isConfigLoaded = true;
        this.isLoadingConfig = false;
      },
      (error) => {
        console.error('Error al cargar configuración:', error);
        this.sendNotification('Error', 'No se pudo cargar la configuración', 'error');
        this.isLoadingConfig = false;
        this.isConfigLoaded = false;
      }
    );
  }

  /**
   * Establecer valores en el formulario
   */
  private setFormValues(data: any): void {
    if (this.form) {
      Object.keys(data).forEach(key => {
        if (this.form.controls[key]) {
          this.form.controls[key].setValue(data[key]);
        }
      });
    }
  }

  /**
   * Abrir modal
   */
  openModal(content: any, size: string = 'lg'): void {
    this.modalConfig.size = size;
    this.modalConfig.backdrop = 'static';
    this.modalConfig.keyboard = false;
    this.activeModal = this.modalService.open(content, this.modalConfig);
  }

  /**
   * Cerrar modal
   */
  closeModal(): void {
    if (this.activeModal) {
      this.activeModal.close();
      this.activeModal = null;
    }
  }

  /**
   * Guardar configuración
   */
  onSubmit(): void {
    this.submitted = true;

    if (this.form.invalid) {
      this.sendNotification('Formulario inválido', 'Por favor, completa todos los campos requeridos', 'error');
      return;
    }

    this.isLoading = true;

    const formData = this.form.value;
    const saveData = this.prepareSaveData(formData);

    if (this.isEditMode) {
      this.updateConfig(saveData);
    } else {
      this.createConfig(saveData);
    }
  }

  /**
   * Preparar datos para guardar
   */
  private prepareSaveData(formData: any): any {
    return {
      ...formData,
      created_by: this.currentUser?.id || null,
      updated_by: this.currentUser?.id || null,
      created_at: this.isEditMode ? this.configData.created_at : new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
  }

  /**
   * Crear nueva configuración
   */
  private createConfig(data: any): void {
    this.apiService.createConfig(data).pipe(
      finalize(() => this.isLoading = false)
    ).subscribe(
      (response) => {
        this.sendNotification('Éxito', 'Configuración creada exitosamente', 'success');
        setTimeout(() => {
          this.regresarALista();
        }, 1500);
      },
      (error) => {
        console.error('Error al crear configuración:', error);
        this.sendNotification('Error', 'No se pudo crear la configuración', 'error');
      }
    );
  }

  /**
   * Actualizar configuración existente
   */
  private updateConfig(data: any): void {
    this.apiService.updateConfig(this.configId, data).pipe(
      finalize(() => this.isLoading = false)
    ).subscribe(
      (response) => {
        this.sendNotification('Éxito', 'Configuración actualizada exitosamente', 'success');
        setTimeout(() => {
          this.regresarALista();
        }, 1500);
      },
      (error) => {
        console.error('Error al actualizar configuración:', error);
        this.sendNotification('Error', 'No se pudo actualizar la configuración', 'error');
      }
    );
  }

  /**
   * Cancelar y regresar
   */
  cancel(): void {
    if (this.form.dirty) {
      if (confirm('¿Seguro que deseas cancelar? Los cambios no guardados se perderán.')) {
        this.regresarALista();
      }
    } else {
      this.regresarALista();
    }
  }

  /**
   * Enviar notificación toast
   */
  private sendNotification(title: string = 'Configuración', message: string, type: string, timeout: number = 3500): void {
    this.toastr[type](message, title, { timeOut: timeout });
  }

  /**
   * Obtener errores de validación
   */
  get formControls() {
    return this.form.controls;
  }

  /**
   * Verificar si un campo tiene error
   */
  hasError(fieldName: string): boolean {
    const control = this.form.controls[fieldName];
    return control.invalid && (control.dirty || control.touched || this.submitted);
  }

  /**
   * Obtener mensaje de error
   */
  getErrorMessage(fieldName: string): string {
    const control = this.form.controls[fieldName];
    if (control.hasError('required')) {
      return 'Este campo es requerido';
    }
    if (control.hasError('minlength')) {
      return `Debe tener al menos ${control.errors.minlength.requiredLength} caracteres`;
    }
    if (control.hasError('pattern')) {
      return 'Formato inválido';
    }
    if (control.hasError('email')) {
      return 'Email inválido';
    }
    return '';
  }

  /**
   * Formatear fecha
   */
  formatDate(dateString: string | undefined): string {
    if (!dateString) {
      return 'Fecha no disponible';
    }
    const dateParts = dateString.split('T');
    if (dateParts.length < 2) {
      return 'Formato de fecha inválido';
    }
    const date = dateParts[0];
    const time = dateParts[1].split('.')[0];
    return `${date} ${time}`;
  }

  /**
   * Eliminar configuración
   */
  deleteConfig(): void {
    if (confirm('¿Estás seguro de eliminar esta configuración?')) {
      this.isLoading = true;
      this.apiService.deleteConfig(this.configId).pipe(
        finalize(() => this.isLoading = false)
      ).subscribe(
        () => {
          this.sendNotification('Éxito', 'Configuración eliminada exitosamente', 'success');
          setTimeout(() => {
            this.regresarALista();
          }, 1500);
        },
        (error) => {
          console.error('Error al eliminar configuración:', error);
          this.sendNotification('Error', 'No se pudo eliminar la configuración', 'error');
        }
      );
    }
  }

  /**
   * Duplicar configuración
   */
  duplicateConfig(): void {
    if (this.configData) {
      this.isEditMode = false;
      this.configData = { ...this.configData };
      this.configData.id = null;
      this.configData.name = `${this.configData.name} (Copia)`;
      this.buildForm();
      this.setFormValues(this.configData);
      this.configId = null;
    }
  }
}

